<template>
  <v-container fluid class="mx-4 px-8">
    <v-row class="mt-2">
      <v-col cols="12" md="2">
        <v-select
          v-model="form.payment_type"
          :items="['Admission', 'Academic']"
          outlined
          dense
          hide-details="auto"
          label="Payment"
          @change="paymentListData = []"
        ></v-select>
      </v-col>
      <v-col cols="2" md="2" v-if="form.payment_type == 'Academic'">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          hide-details="auto"
          label="USN"
        ></v-text-field>
      </v-col>
      <v-col cols="2" md="2" v-if="form.payment_type == 'Admission'">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
        ></v-text-field>
      </v-col>
      <v-col cols="2" md="2">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Programme"
        ></v-autocomplete>
      </v-col>
      <v-col cols="2" md="2" v-if="showPartner">
        <v-select
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Partner"
        ></v-select>
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="getPaymentList"
        >
          Search
        </v-btn>
        <v-btn
          v-if="currentUser.role != 'Reviewer'"
          :disabled="!paymentListData.length"
          class="accent white--text ml-2"
          @click="download"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-btn v-else class="accent white--text ml-2" @click="dialog = true">
          Upload Csv
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="computedHeaders"
            :items="paymentListData"
            item-key="stn"
            fixed-header
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.plan_amount_balance`]="{ item }">
              {{
                item.plan_amount_balance
                  ? item.plan_amount_balance
                  : item.amount
              }}
            </template>
            <template v-slot:[`item.set_payment`]="{ item }">
              <v-icon
                class="mr-2"
                color="accent"
                @click="navigateToPaymentOptions(item)"
              >
                mdi-dots-horizontal
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" persistent width="800">
      <formDialog
        v-if="dialog"
        @update="(dialog = false), getPaymentList()"
        @error="errorDownload"
        :title="form.payment_type"
        :partnerId="form.partner_id"
      />
    </v-dialog>
    <v-dialog v-model="showErrorDialog" width="450" persistent>
      <v-card class="secondary">
        <v-card-title class="primary white--text"> Upload Failed </v-card-title>
        <v-card-text class="mt-4">
          <div class="subtitle-2">
            Your upload could not be completed because some records contain
            errors.
            <br /><br />
            Please download the error file, correct the issues, and try
            uploading again.
          </div>
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <v-btn class="primary" @click="downloadErrorCsv"> Download</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import formDialog from "@/components/bulkPayment/form.vue";
import { rules } from "@/helpers/rules";
import { bulkPaymentHeaders } from "@/constants/headers";
import env from "@/helpers/env";

export default {
  components: {
    formDialog,
  },
  data() {
    return {
      rules,
      form: {
        payment_type: "Admission",
        status: "Pending"
      },
      bulkPaymentHeaders,
      dialog: false,
      qualifications: [],
      programs: [],
      partners: [],
      paymentListData: [],
      loading: false,
      env,
      showErrorDialog: false,
      errorCsvBlob: null,
    };
  },
  computed: {
    showPartner() {
      return (
        this.currentUser.partner_id == 1 ||
        (this.partners.length > 1 && this.currentUser.partner_level != "S")
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    computedHeaders() {
      const headers = [];

      if (this.form.payment_type === "Admission") {
        headers.push(
          {
            text: "Application ID",
            value: "application_id",
            class: "black--text font-weight-bold secondary",
          },
          ...this.bulkPaymentHeaders,
          {
            text: "Set Payment",
            value: "set_payment",
            class: "black--text font-weight-bold secondary",
          }
        );
      } else {
        headers.push(
          {
            text: "USN",
            value: "usn_id",
            class: "black--text font-weight-bold secondary",
          },
          ...this.bulkPaymentHeaders
        );
      }

      return headers;
    },
  },
  async created() {
    if (
      this.currentUser.partner_payment_required ||
      this.currentUser.role == "Reviewer"
    ) {
      this.$store.commit("setBreadcrumbs", [{ text: "Set Bulk Payments" }]);
      this.init();
    } else {
      this.$router.push("/");
    }
  },
  methods: {
    async init() {
      await Promise.all([this.getPrograms(), this.showPartnerFilter()]);
      this.getPaymentList();
    },
    objectToQueryParams(obj) {
      return Object.entries(obj)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== ""
        )
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&");
    },
    async getPaymentList() {
      this.loading = true;
      this.paymentListData = [];
      try {
        const queryString = this.objectToQueryParams(this.form);
        let url = null;
        if (this.form.payment_type == "Admission") {
          url = `/student_admit_plans?${queryString}&!plan_amount_balance=0`;
        } else {
          url = `/list_student_invoices_report?${queryString}&types=Term Fee,Annual,Re-Admission Fee`;
        }
        const res = await this.$axios.get(url);
        this.paymentListData = res.data.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getPrograms() {
      try {
        let url = null;
        if (this.currentUser.role != "Reviewer") {
          url = `/get_partner_programs/${this.currentUser.partner_id}`;
        } else {
          url = `programs`;
        }
        const response = await this.$axios.get(url);
        this.programs = response.data.data;
        if (this.programs.length === 1) {
          this.form.program_id = this.programs[0].id;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },

    async showPartnerFilter() {
      await this.getPartners();
      if (
        this.currentUser.role === "Business" &&
        this.currentUser.partner_level === "P"
      ) {
        this.partners = this.partners.filter(
          (item) =>
            item.id === this.currentUser.partner_id ||
            item.parent_id === this.currentUser.partner_id
        );
      }
    },
    async getPartners() {
      try {
        const response = await this.$axios.get(
          "/partners?adm_applicant_fee_collect=true"
        );
        this.partners = response.data.data;
        if (this.currentUser.partner_id != 1) {
          this.form.partner_id = this.currentUser.partner_id;
        } else {
          this.form.partner_id = response.data.data[0].id;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getPartnerName(code) {
      const partner = this.partners.find((partner) => partner.code === code);
      return partner ? partner.name : "";
    },
    async download() {
      const queryString = this.objectToQueryParams(this.form);
      this.$axios
        .get(`/download_bulk_payments?${queryString}&types=Term Fee,Annual,Re-Admission Fee&header=Amount Paid`, {
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Set Bulk Payments.csv`);
          document.body.appendChild(link);
          link.click();
        });
    },
    navigateToPaymentOptions(item) {
      window.open(
        `${this.env.application_url}/payment-options?id=${item.applicant_detail_id}&partner_payment=true`,
        "_self"
      );
    },
    downloadErrorCsv() {
      const url = window.URL.createObjectURL(this.errorCsvBlob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "Set Bulk Payment Errors.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showErrorDialog = false;
      this.errorCsvBlob = null;
      this.getPaymentList();
    },
    errorDownload(data) {
      this.$store.commit("showSnackbar", {
        message: "Upload Failed",
        color: "error",
      });
      this.dialog = false;
      this.errorCsvBlob = new Blob([data]);
      this.showErrorDialog = true;
    },
  },
};
</script>

<style scoped></style>
